import type { ReactNode } from 'react';

import { BrandMark, HangingTag } from '@/components/BrandMark';
import { palette } from '@/theme';

/**
 * The screens a person meets before they are inside: the brand panel on the left, the form on the
 * right, one column below `lg`.
 *
 * The panel is the phone's sign-in hero (`AuthShell`): the AttireRent tag hanging on its string over
 * the stitched cloth, on the theme's deep band. It needs no asset, no `public/` file and no network
 * request, since the tag is the core's shapes and the cloth is CSS (`.auth-band` in `style.css`).
 */
export interface AuthPanel {
  heading: string;
  blurb: string;
}

export function AuthLayout({
  panel,
  title,
  subtitle,
  children,
}: {
  panel: AuthPanel;
  title: string;
  subtitle: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="bg-light min-h-screen">
      <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
        {/*
          `hidden lg:flex` keeps the panel off narrow screens entirely: on a counter tablet in
          portrait the form should have the whole viewport, not compete with decoration for it.
        */}
        <div className="auth-band hidden flex-col items-center lg:flex">
          {/* Hung from the very top of the screen, the string running up out of view. */}
          <HangingTag width={168} />
          <div className="mt-8 max-w-sm px-8 pb-12 text-center">
            <h1 className="auth-band-title mb-3">{panel.heading}</h1>
            <p className="auth-band-blurb">{panel.blurb}</p>
          </div>
        </div>

        <div className="flex items-center justify-center p-8">
          <div className="w-full max-w-md">
            <div className="mb-6 text-center">
              {/*
                Only below `lg`, where the brand panel is not on screen. Above it the panel already
                says AttireRent an inch to the left, and repeating it twice on one screen reads as a
                mistake — but dropping it outright would leave a narrow counter tablet, which never
                sees the panel, with no branding at all.
              */}
              <div className="mb-4 lg:hidden">
                <Wordmark />
              </div>

              <h2 className="mb-1 text-xl font-bold text-title">{title}</h2>
              <p className="text-sm text-default">{subtitle}</p>
            </div>

            {/*
              There is deliberately no `Card` around the form. The illustration variant sits the
              fields directly on the canvas; keeping the card would produce a panel inside a panel.
            */}
            {children}
          </div>
        </div>
      </div>
    </div>
  );
}

/** The same mark the shell uses, so the auth screens and the app agree about what this is. */
export function Wordmark() {
  return (
    <div
      style={{ display: 'inline-flex', alignItems: 'center', gap: 10, justifyContent: 'center' }}
    >
      <BrandMark size={34} tone={palette.teal} />
      {/* `text-title` rather than `palette.ink`: the latter is a fixed slate and vanishes on dark. */}
      <span
        className="text-title"
        style={{ fontFamily: 'var(--font-serif)', fontWeight: 600, fontSize: 22 }}
      >
        AttireRent
      </span>
    </div>
  );
}
